import axios from "axios";
import { useEffect, useRef, useState } from "react";
import MainLayout from "../../Layout/MainLayout";
import BASEURL from "../../Config/global";
import Accordion from "react-bootstrap/Accordion";
import { useDispatch } from "react-redux";
import CustomAudioCard from "../../Components/CustomAudioCard";
import AudioStrip from "../../Components/AudioStrip";
import { userLogout } from "../../Store/Slices/APIs";
import "./style.css";
import Common from "../../Util/common";
import { useLocation } from "react-router";
import CustomCard from "../../Components/CustomCard";
import { useTranslation } from "react-i18next";
import { translateEnglishToSpanishBatch } from "../../Util/translator";

const FeaturedMixes = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const alwaysOpenStates = useRef(location?.state?.from === "/featured-mixes-single" ? JSON.parse(localStorage.getItem('featured-state')) : ["0"]);

  const [categorizedSoundsData, setCategorizedSoundsData] = useState([]);
  const [translatedCategoryNames, setTranslatedCategoryNames] = useState({});
  const [translatedSubCategoryNames, setTranslatedSubCategoryNames] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    dispatch(userLogout())
  }, [])

  useEffect(() => {
    if (location?.state?.from === "/featured-mixes-single") {
      alwaysOpenStates.current = JSON.parse(localStorage.getItem('featured-state'));
    } else {
      localStorage.setItem('featured-state', JSON.stringify(alwaysOpenStates.current));
    }
    fetchData();
  }, []);

  // Translate only category and sub-category names (not media titles)
  useEffect(() => {
    const applyCategoryTranslations = async () => {
      // Only translate when UI is Spanish
      if (i18n.language !== "es") {
        setTranslatedCategoryNames({});
        setTranslatedSubCategoryNames({});
        setIsTranslating(false);
        return;
      }

      if (!categorizedSoundsData || categorizedSoundsData.length === 0) return;

      setIsTranslating(true);

      try {
        const itemsForTranslation = [];

        // Collect top-level category names
        categorizedSoundsData.forEach((cat) => {
          if (cat?.id && cat?.name) {
            itemsForTranslation.push({
              id: cat.id,
              type: "category",
              name: cat.name,
            });
          }
        });

        // Collect sub-category names
        categorizedSoundsData.forEach((cat) => {
          if (cat?.children_category_data && cat.children_category_data.length > 0) {
            cat.children_category_data.forEach((sub) => {
              if (sub?.id && sub?.name) {
                itemsForTranslation.push({
                  id: sub.id,
                  type: "subCategory",
                  name: sub.name,
                });
              }
            });
          }
        });

        if (itemsForTranslation.length === 0) {
          setIsTranslating(false);
          return;
        }

        const texts = itemsForTranslation.map((item) => item.name);
        const batchResult = await translateEnglishToSpanishBatch(texts);

        if (!batchResult.success || !batchResult.translatedTexts) {
          throw new Error(batchResult.error || "Batch translation failed");
        }

        const categoryResults = [];
        const subCategoryResults = [];

        batchResult.translatedTexts.forEach((translated, idx) => {
          const item = itemsForTranslation[idx];
          if (!item || !translated) return;
          const mapped = { id: item.id, name: translated };
          if (item.type === "category") {
            categoryResults.push(mapped);
          } else if (item.type === "subCategory") {
            subCategoryResults.push(mapped);
          }
        });

        const catMap = {};
        categoryResults.forEach((item) => {
          if (item && item.id) {
            catMap[item.id] = item.name;
          }
        });

        const subCatMap = {};
        subCategoryResults.forEach((item) => {
          if (item && item.id) {
            subCatMap[item.id] = item.name;
          }
        });

        setTranslatedCategoryNames(catMap);
        setTranslatedSubCategoryNames(subCatMap);
      } catch (e) {
        console.error("FeaturedMixes category translation failed:", e);
        setTranslatedCategoryNames({});
        setTranslatedSubCategoryNames({});
      } finally {
        setIsTranslating(false);
      }
    };

    applyCategoryTranslations();
  }, [i18n.language, categorizedSoundsData]);

  const fetchData = async () => {
    try {
      // http://backend.dev.relax.scofa.com/api/sounds/mixture-categories/'
      const response = await axios.get(`${BASEURL}/api/sounds/mixture-categories/`);
      if (!response?.data?.error && response?.data?.data?.length > 0) {
        const sortedData = response.data.data.sort(function (a, b) { return a.sequence - b.sequence })
        setCategorizedSoundsData(sortedData);
      } else {
        console.log(response.data.message);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleAccordionState = (event, index) => {
    console.clear();
    const array = JSON.parse(localStorage.getItem('featured-state')) || [];
    if (event.target.className === 'accordion-button collapsed') {
      array.push(index.toLocaleString());
      localStorage.setItem('featured-state', JSON.stringify(array));
    } else {
      const indexToRemove = array.findIndex(item => item === index.toLocaleString());
      const result = indexToRemove !== -1 ? array.slice(0, indexToRemove).concat(array.slice(indexToRemove + 1)) : [];
      localStorage.setItem('featured-state', JSON.stringify(result));
    }
  }

  return (
    <>
      <MainLayout title={t("nav.featuredMixes")}>
        <div className="row mb-3">
          <div className="col-12">
            <Accordion
              defaultActiveKey={alwaysOpenStates.current}
              alwaysOpen
              className="customAccordion audio-accordian"
            >
              {isTranslating ? (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : categorizedSoundsData && categorizedSoundsData?.map((categorizedData, index) => {
                let sortedChildrens = categorizedData.children_category_data
                if (sortedChildrens > 0) {
                  sortedChildrens = sortedChildrens.sort(function (a, b) { return a.sequence - b.sequence })
                }
                return (
                  <Accordion.Item eventKey={index.toLocaleString()} key={categorizedData.id} >
                    <Accordion.Header
                      style={{
                        backgroundImage: `url(${categorizedData?.banner})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundColor: '#1c3664',
                      }}
                      onClick={(e) => handleAccordionState(e, index)}
                    >
                      {(() => {
                        const categoryName = translatedCategoryNames[categorizedData.id] || categorizedData?.name || '';
                        return categoryName.toLowerCase() === 'featured' 
                          ? t("home.featured") 
                          : categoryName;
                      })()}
                    </Accordion.Header>
                    <Accordion.Body className={index > 0 ? 'accordion-body-spaced' : ''}>
                      <div className={index == 0 ? 'homeboxesWrapper  ' : 'audios-rows audioStrips stripedRows'}>
                        {categorizedData?.category_data?.length > 0 && categorizedData?.category_data?.map((item, idx) => (
                          index == 0 ? (
                            <div className="mb-2">
                              <CustomCard key={index}
                                title={item.title}
                                image={`${item.banner}`}
                                alt={"In The News"}
                                item={item}
                                type={Common.CATEGORIES['mix']} />
                            </div>
                          ) : <AudioStrip key={idx} item={item} type={Common.CATEGORIES['mix']} />
                        ))}
                      </div>
                      {
                        sortedChildrens?.length > 0 ? <div className="sub-category-accordian">
                          {sortedChildrens?.length > 0 && sortedChildrens?.map((categorizedDataiii, index2) => (
                            <Accordion.Item eventKey={Math.random()} key={index2}>
                              <Accordion.Header
                                className="sub-category-header"
                                style={{
                                  backgroundImage: `url(${categorizedDataiii?.banner})`,
                                  backgroundSize: 'cover',
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right center',
                                  backgroundColor: '#ffffff2b',
                                }}
                              >
                                {(() => {
                                  const subCategoryName = translatedSubCategoryNames[categorizedDataiii.id] || categorizedDataiii?.name || '';
                                  return subCategoryName.toLowerCase() === 'featured' 
                                    ? t("home.featured") 
                                    : subCategoryName;
                                })()}
                              </Accordion.Header>
                              <Accordion.Body>
                                <div className="audios-rows audioStrips stripedRows">
                                  {categorizedDataiii?.category_data?.length > 0 && categorizedDataiii?.category_data?.map((item, idx) => (
                                    <AudioStrip key={idx} item={item} type={Common.CATEGORIES['mix']} />
                                  ))}
                                </div>
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </div> : null
                      }

                    </Accordion.Body>
                  </Accordion.Item>
                )
              })}
            </Accordion>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default FeaturedMixes;